import { checkEagleLoadFile, EAGLELOAD_MAX_ROWS, newLoadCount } from "@flexfreight/core";
import { Banner, Button, Combobox, DataTable, FileField, KpiTile, notify, PageHeader, Section, useConfirm, useUrlFilters, type Column } from "@flexfreight/ui";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";

export type ImportResult = {
  dryRun: boolean;
  rowCount: number;
  valid: number;
  imported: number;
  duplicates: string[];
  costMissing: number;
  shipmentIds: string[];
  errors: { line: number; loadId: string; message: string }[];
};
type RowError = ImportResult["errors"][number];

const errorText = (e: unknown): string => {
  if (!(e instanceof ApiError)) return "Couldn't process this file. Try again.";
  if (e.status === 429) return "Too many imports in the last minute. Wait a minute and try again.";
  if (e.status === 413) return "That file is too large. Split the export by date range.";
  if (e.status === 415) return "That file isn't CSV. Export the loads from EagleLoad as CSV.";
  return e.message;
};
const FILTERS = { org: "", name: "" };
const COLUMNS: Column<RowError>[] = [
  { key: "line", header: "Line", numeric: true, width: "5rem", render: (e) => e.line },
  { key: "load", header: "Load", primary: true, width: "10rem", render: (e) => e.loadId || "-" },
  { key: "msg", header: "Problem", render: (e) => e.message },
];

/** Freight > Shipment import: load a customer's shipment history from an EagleLoad CSV. Dry run first, then import. */
export function ImportPage() {
  const f = useUrlFilters(FILTERS, useSearchParams());
  const { org: orgId, name: orgName } = f.values;
  const { confirm, dialog } = useConfirm();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const run = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) =>
      api<ImportResult>(`/v1/admin/orgs/${orgId}/shipments/import/eagleload${dryRun ? "?dryRun=true" : ""}`, { method: "POST", body: new Blob([file!.text], { type: "text/csv" }) }),
    onSuccess: (r) => {
      setResult(r);
      if (!r.dryRun) notify.success(`Imported ${r.imported} load${r.imported === 1 ? "" : "s"} into ${orgName}`, r.duplicates.length ? `${r.duplicates.length} already imported earlier were skipped.` : undefined);
    },
    onError: (e) => notify.error("Import didn't run", errorText(e)),
  });
  const pick = async (fl: File | undefined) => {
    setResult(null);
    if (!fl) return setFile(null);
    const err = checkEagleLoadFile(fl);
    setFileError(err);
    setFile(err ? null : { name: fl.name, text: await fl.text() });
  };
  const toImport = result?.dryRun ? newLoadCount(result) : 0;
  const doImport = async () => {
    if (await confirm({ title: `Import ${toImport} load${toImport === 1 ? "" : "s"} into ${orgName}?`, body: "They are added to the customer's shipment history with the carrier cost and price from the file. Re-importing the same file later skips them.", confirmLabel: "Import" })) run.mutate({ dryRun: false });
  };
  return (
    <section className="ffa-page">
      <PageHeader
        title="Shipment import"
        subtitle={`Bring a customer's past loads over from EagleLoad: export them as CSV (up to ${EAGLELOAD_MAX_ROWS} loads, 2 MB), check with a dry run, then import. Loads already imported are skipped.`}
        secondaryActions={orgId ? <a className="ffx-btn" href={`/v1/admin/orgs/${orgId}/shipments/import/eagleload/template`}>Download template</a> : null}
      />
      <Section title="Import a file" description="Nothing is saved until you import after the dry run.">
        {orgId ? (
          <div className="ffx-actions">
            <span>
              Importing into <Link to={`/customers/${orgId}`}>{orgName || "this customer"}</Link>.
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                f.set({ org: "", name: "" });
                setResult(null);
              }}
            >
              Change customer
            </Button>
          </div>
        ) : null}
        <div className="ffx-fields">
          {orgId ? null : (
            <Combobox
              label="Customer"
              hint="Type the company name."
              emptyText="No customer with that name."
              load={async (text) => (await api<{ orgs: { id: string; name: string }[] }>(`/v1/admin/orgs?${new URLSearchParams({ q: text })}`)).orgs.map((o) => ({ value: o.id, label: o.name }))}
              onSelect={(o) => f.set({ org: o.value, name: o.label })}
            />
          )}
          <FileField ref={input} label="EagleLoad CSV" accept=".csv,text/csv" onChange={(e) => void pick(e.target.files?.[0])} error={fileError ?? undefined} />
        </div>
        <div className="ffx-actions">
          <Button variant="primary" disabled={!orgId || !file} loading={run.isPending && run.variables?.dryRun} onClick={() => run.mutate({ dryRun: true })}>Dry run</Button>
          {result?.dryRun && toImport > 0 ? <Button loading={run.isPending && !run.variables?.dryRun} onClick={() => void doImport()}>Import {toImport} load{toImport === 1 ? "" : "s"}</Button> : null}
        </div>
      </Section>
      {result ? (
        <>
          <Banner tone={result.dryRun ? "info" : "success"}>
            {result.dryRun
              ? toImport === 0
                ? "Dry run complete. There are no new loads to import from this file."
                : `Dry run complete. Nothing was saved. Review below, then import ${toImport} new load${toImport === 1 ? "" : "s"}.`
              : `Imported ${result.imported} load${result.imported === 1 ? "" : "s"}.`}
          </Banner>
          <div className="ffa-kpis">
            <KpiTile label="Rows" value={String(result.rowCount)} sub={file?.name} />
            <KpiTile label={result.dryRun ? "New loads" : "Imported"} value={String(result.dryRun ? toImport : result.imported)} />
            <KpiTile label="Already imported" value={String(result.duplicates.length)} sub="skipped" />
            <KpiTile label="Rows with problems" value={String(result.errors.length)} sub="not imported" tone={result.errors.length > 0 ? "danger" : undefined} />
            <KpiTile label="No carrier cost" value={String(result.costMissing)} sub="cost recorded as the price" tone={result.costMissing > 0 ? "warning" : undefined} />
          </div>
          {result.errors.length ? (
            <Section title="Rows with problems" description="Fix these in the export and run it again; the rest import without them." flush>
              <DataTable caption="Rows with problems" columns={COLUMNS} rows={result.errors} rowKey={(e) => `${e.line}-${e.loadId}`} maxHeight="none" />
            </Section>
          ) : null}
        </>
      ) : null}
      {dialog}
    </section>
  );
}
